import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack, useTheme } from '@mui/material';
import { useState } from 'react';
import type { ReactNode } from 'react';

/**
 * One-shot confirmation modal for destructive actions (DLQ replay, secret
 * rotation, facility deactivation, etc.). The body shows what's about to
 * happen; the action button label is required so we never default to a
 * generic "Confirm" that can be misread.
 *
 * `tone='danger'` switches the action button to the error palette so the
 * visual cost matches the cost of a misclick.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  actionLabel,
  cancelLabel = 'Cancel',
  tone = 'neutral',
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: ReactNode;
  body: ReactNode;
  actionLabel: ReactNode;
  cancelLabel?: ReactNode;
  tone?: 'neutral' | 'danger';
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}) {
  const theme = useTheme();
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setSubmitting(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: theme.radius.lg + 'px', boxShadow: theme.elevation.overlay },
      }}
    >
      <DialogTitle sx={{ pb: 0.5, fontWeight: 600, letterSpacing: -0.2 }}>{title}</DialogTitle>
      <DialogContent sx={{ pb: 1 }}>
        <DialogContentText component="div" sx={{ color: theme.palette.text.secondary, fontSize: 14 }}>
          {body}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Stack direction="row" spacing={1} sx={{ width: '100%' }} justifyContent="flex-end">
          <Button onClick={onClose} disabled={submitting} color="inherit">
            {cancelLabel}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={submitting}
            variant="contained"
            sx={
              tone === 'danger'
                ? { bgcolor: theme.palette.status.danger, '&:hover': { bgcolor: theme.palette.status.danger } }
                : undefined
            }
          >
            {actionLabel}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
